import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import {
  History as HistoryIcon,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Download,
  Loader2,
  RefreshCw,
  Filter,
  MapPin,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import Layout from '../components/Layout';
import SyncProgressBar from '../components/SyncProgressBar';
import { useLocationContext } from '../context/LocationContext';

/** Format week range as "Feb 26 - Mar 4" with inclusive end (end date - 1 day) */
function formatWeekRange(weekStart, weekEnd) {
  const fmt = (iso) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  const end = new Date(weekEnd + 'T00:00:00');
  end.setDate(end.getDate() - 1);
  return `${fmt(weekStart)} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

export default function History() {
  const queryClient = useQueryClient();
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [datePreset, setDatePreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const { selectedLocationId, currentLocation } = useLocationContext();

  // Compute dateFrom/dateTo from preset or custom
  const { dateFrom, dateTo } = useMemo(() => {
    if (datePreset === 'custom') {
      return { dateFrom: customFrom || undefined, dateTo: customTo || undefined };
    }
    if (datePreset === 'all') return { dateFrom: undefined, dateTo: undefined };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let from;
    switch (datePreset) {
      case 'this_week': {
        from = new Date(today);
        const day = from.getDay();
        const diff = (day === 0 ? 6 : day - 1); // Monday = 0 offset
        from.setDate(from.getDate() - diff);
        break;
      }
      case 'last_week': {
        from = new Date(today);
        const day = from.getDay();
        const diff = (day === 0 ? 6 : day - 1);
        from.setDate(from.getDate() - diff - 7);
        const to = new Date(from);
        to.setDate(to.getDate() + 6);
        return { dateFrom: from.toISOString().split('T')[0], dateTo: to.toISOString().split('T')[0] };
      }
      case 'last_30': {
        from = new Date(today);
        from.setDate(from.getDate() - 30);
        break;
      }
      case 'last_90': {
        from = new Date(today);
        from.setDate(from.getDate() - 90);
        break;
      }
      default:
        return { dateFrom: undefined, dateTo: undefined };
    }
    return { dateFrom: from.toISOString().split('T')[0], dateTo: undefined };
  }, [datePreset, customFrom, customTo]);

  // Sync Zuper data mutation
  const syncMutation = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post('/api/reminders/sync', { weekOffset: 0 });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      queryClient.invalidateQueries({ queryKey: ['history'] });
    },
  });

  // Fetch analytics
  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['analytics', selectedLocationId, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const qs = params.toString();
      const { data } = await axios.get(`/api/reminders/analytics${qs ? '?' + qs : ''}`);
      return data;
    },
  });

  const totals = analytics?.totals || {};
  const campaigns = analytics?.campaigns || [];
  const topCustomers = analytics?.topCustomers || [];

  const deliveryRate = totals.total_messages_sent > 0
    ? Math.round(((totals.total_delivered + totals.total_read) / totals.total_messages_sent) * 100)
    : 0;

  const handleExport = async (campaignId = null) => {
    const url = campaignId
      ? `/api/reminders/export?campaignId=${campaignId}`
      : '/api/reminders/export';
    window.open(url, '_blank');
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const s = isoString.endsWith('Z') ? isoString : isoString.replace(' ', 'T') + 'Z';
    const d = new Date(s);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <Layout>
      {/* Header */}
      <header className="bg-white border-b border-brand-light/60 p-3 md:p-4 flex justify-between items-center z-10 shadow-sm relative">
        <SyncProgressBar isSyncing={syncMutation.isPending} />
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-brand-light to-white rounded-xl flex items-center justify-center shadow-sm">
            <HistoryIcon className="w-5 h-5 text-brand-primary" />
          </div>
          <div>
            <h1 className="text-base md:text-xl font-bold text-brand-dark tracking-tight">Booking Analytics</h1>
            <p className="text-xs text-brand-neutral hidden md:block">Campaign analytics and reminder performance</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {currentLocation && (
            <span className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-brand-primary bg-brand-light/60 rounded-lg">
              <MapPin className="w-3 h-3" />
              {currentLocation.name}
            </span>
          )}
          <button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-brand-dark bg-white border border-brand-light rounded-xl hover:bg-brand-light transition-colors shadow-sm disabled:opacity-50"
            title="Sync reminder history from database"
          >
            <RefreshCw className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{syncMutation.isPending ? 'Syncing...' : 'Sync'}</span>
          </button>
          <button
            onClick={() => handleExport()}
            className="flex items-center gap-1.5 px-3 md:px-4 py-2 text-xs md:text-sm font-medium text-brand-dark bg-white border border-brand-light rounded-xl hover:bg-brand-light transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export CSV</span>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto p-3 md:p-6 bg-[#f5f8f6]">
        <div className="max-w-full mx-auto space-y-4">

          {/* Date Range Picker */}
          <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 p-3 md:p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="w-4 h-4 text-brand-neutral" />
              <span className="text-xs font-medium text-brand-dark mr-1">Period:</span>
              {[
                { value: 'all', label: 'All Time' },
                { value: 'this_week', label: 'This Week' },
                { value: 'last_week', label: 'Last Week' },
                { value: 'last_30', label: 'Last 30 Days' },
                { value: 'last_90', label: 'Last 90 Days' },
                { value: 'custom', label: 'Custom' },
              ].map(p => (
                <button
                  key={p.value}
                  onClick={() => setDatePreset(p.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${datePreset === p.value
                    ? 'bg-brand-primary text-white shadow-sm'
                    : 'bg-brand-light text-brand-neutral hover:bg-brand-primary/10'
                  }`}
                >
                  {p.label}
                </button>
              ))}
              {datePreset === 'custom' && (
                <div className="flex items-center gap-2 ml-2">
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="px-2 py-1.5 text-xs border border-brand-light rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-primary/30"
                  />
                  <span className="text-xs text-brand-neutral">to</span>
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="px-2 py-1.5 text-xs border border-brand-light rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-primary/30"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Overall Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 p-3 md:p-4 text-center hover:shadow-md transition-shadow">
              <p className="text-2xl md:text-3xl font-bold text-blue-600">{totals.total_messages_sent || 0}</p>
              <p className="text-xs text-brand-neutral mt-1">Messages Sent</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 p-3 md:p-4 text-center hover:shadow-md transition-shadow">
              <p className="text-2xl md:text-3xl font-bold text-brand-dark">{totals.customers_messaged || 0}</p>
              <p className="text-xs text-brand-neutral mt-1">Customers Reached</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 p-3 md:p-4 text-center hover:shadow-md transition-shadow">
              <p className="text-2xl md:text-3xl font-bold text-green-600">{totals.booked_via_link || 0}</p>
              <p className="text-xs text-brand-neutral mt-1">Booked via Link</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 p-3 md:p-4 text-center hover:shadow-md transition-shadow">
              <p className="text-2xl md:text-3xl font-bold text-brand-primary">{totals.conversion_rate || 0}%</p>
              <p className="text-xs text-brand-neutral mt-1">Conversion Rate</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-brand-light p-3 md:p-4 text-center col-span-2 md:col-span-1">
              <p className="text-2xl md:text-3xl font-bold text-amber-600">{deliveryRate}%</p>
              <p className="text-xs text-brand-neutral mt-1">Delivery Rate</p>
            </div>
          </div>

          {/* Campaign Analytics */}
          {(
            <div className="bg-white rounded-2xl shadow-sm border border-brand-light overflow-hidden">

              {analyticsLoading ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-brand-primary" />
                </div>
              ) : campaigns.length === 0 ? (
                <div className="p-8 text-center text-brand-neutral text-sm">No campaigns yet</div>
              ) : (
                <div className="divide-y divide-brand-light/50">
                  {campaigns.map((c) => {
                    const convRate = c.customers_messaged > 0
                      ? Math.round((c.booked_via_link / c.customers_messaged) * 100)
                      : 0;
                    const isExpanded = expandedCampaign === c.id;

                    return (
                      <div key={c.id}>
                        <div
                          className="p-4 cursor-pointer hover:bg-brand-light/30 transition-colors"
                          onClick={() => setExpandedCampaign(isExpanded ? null : c.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-sm font-medium text-brand-dark">
                                  {formatWeekRange(c.week_start, c.week_end)}
                                </span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                  {c.status}
                                </span>
                              </div>

                              {/* Conversion progress bar */}
                              <div className="flex items-center gap-3">
                                <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-brand-primary to-green-500 rounded-full transition-all"
                                    style={{ width: `${convRate}%` }}
                                  />
                                </div>
                                <span className="text-xs font-bold text-brand-dark w-10 text-right">{convRate}%</span>
                              </div>

                              {/* Quick stats */}
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-brand-neutral">
                                <span className="text-blue-600">{c.messages_sent || 0} messages sent</span>
                                <span>{c.customers_messaged || 0} customers reached</span>
                                <span className="text-green-600">{c.booked_via_link || 0} booked via link</span>
                                {c.messages_failed > 0 && <span className="text-red-600">{c.messages_failed} failed</span>}
                              </div>
                            </div>

                            <div className="flex items-center gap-2 ml-4">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleExport(c.id); }}
                                className="p-1.5 rounded-lg hover:bg-brand-light text-brand-neutral hover:text-brand-primary transition-colors"
                                title="Export this campaign"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-brand-neutral" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-brand-neutral" />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Expanded: message delivery stats */}
                        {isExpanded && (
                          <div className="px-4 pb-4 bg-brand-light/20 border-t border-brand-light/50">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 text-xs">
                              <div className="bg-white rounded-lg p-2 border border-brand-light text-center">
                                <p className="font-bold text-blue-600">{c.messages_sent || 0}</p>
                                <p className="text-brand-neutral">Messages Sent</p>
                              </div>
                              <div className="bg-white rounded-lg p-2 border border-brand-light text-center">
                                <p className="font-bold text-green-600">{c.messages_delivered || 0}</p>
                                <p className="text-brand-neutral">Delivered</p>
                              </div>
                              <div className="bg-white rounded-lg p-2 border border-brand-light text-center">
                                <p className="font-bold text-emerald-600">{c.messages_read || 0}</p>
                                <p className="text-brand-neutral">Read</p>
                              </div>
                              <div className="bg-white rounded-lg p-2 border border-brand-light text-center">
                                <p className="font-bold text-red-600">{c.messages_failed || 0}</p>
                                <p className="text-brand-neutral">Failed</p>
                              </div>
                            </div>
                            {c.last_synced_at && (
                              <p className="text-[10px] text-brand-neutral mt-2">Last synced: {formatDateTime(c.last_synced_at)}</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

        </div>
      </main>
    </Layout>
  );
}
